/**
 * cart-bff `/v1/payment-methods/*` endpoints — Phase 2 saved-card surface.
 *
 * All three calls are gated by `PAYMENT_METHODS_PUBLIC_ENABLED` on the BFF;
 * a 503 with `code: phase_2_disabled` surfaces as a `BffError` and should
 * be handled by the saved-cards UI with a "feature unavailable" message.
 */

import type {
  PaymentMethodAuthoriseDetailsRequestBody,
  PaymentMethodAuthoriseRequestBody,
  PaymentMethodAuthoriseResponse,
  PaymentMethodSaveRequestBody,
  PaymentMethodSaveResponse,
  PaymentMethodsListResponse,
  TokenizeSessionResponse,
} from '../types/api';
import type { HttpClient } from './http';

const BASE = '/v1/payment-methods';

export interface PaymentMethodsApi {
  /** Mints a zero-auth Adyen session for tokenizing a card without charging. */
  createTokenizeSession(input: {
    tokenId: string;
    countryCode: string;
  }): Promise<TokenizeSessionResponse>;
  /**
   * Advanced-flow zero-auth `/payments` tokenize. Relays the CSE-encrypted card
   * so the server can read the exact `recurringDetailReference`. Bearer-auth'd
   * with the `tokenizeSessionToken`.
   */
  authorise(input: {
    tokenizeSessionToken: string;
    body: PaymentMethodAuthoriseRequestBody;
  }): Promise<PaymentMethodAuthoriseResponse>;
  /** 3DS2 continuation for a tokenize started by `authorise`. */
  authoriseDetails(input: {
    tokenizeSessionToken: string;
    body: PaymentMethodAuthoriseDetailsRequestBody;
  }): Promise<PaymentMethodAuthoriseResponse>;
  /** Persists the tokenized card. Uses the `tokenizeSessionToken` returned above as bearer. */
  save(input: {
    tokenizeSessionToken: string;
    body: PaymentMethodSaveRequestBody;
  }): Promise<PaymentMethodSaveResponse>;
  /** Lists the customer's saved cards. SmartHQ callers request the single-card projection. */
  list(input: { tokenId: string; view?: 'smarthq' }): Promise<PaymentMethodsListResponse>;
}

export function createPaymentMethodsApi(http: HttpClient): PaymentMethodsApi {
  return {
    createTokenizeSession: ({ tokenId, countryCode }) =>
      http.request<TokenizeSessionResponse>('POST', `${BASE}/tokenize-session`, {
        tokenId,
        countryCode,
        body: {},
      }),

    authorise: ({ tokenizeSessionToken, body }) =>
      http.request<PaymentMethodAuthoriseResponse>('POST', `${BASE}/authorise`, {
        bearerToken: tokenizeSessionToken,
        body,
      }),

    authoriseDetails: ({ tokenizeSessionToken, body }) =>
      http.request<PaymentMethodAuthoriseResponse>('POST', `${BASE}/authorise-details`, {
        bearerToken: tokenizeSessionToken,
        body,
      }),

    save: ({ tokenizeSessionToken, body }) =>
      http.request<PaymentMethodSaveResponse>('POST', `${BASE}/save`, {
        bearerToken: tokenizeSessionToken,
        body,
      }),

    list: ({ tokenId, view }) =>
      http.request<PaymentMethodsListResponse>('GET', BASE, {
        tokenId,
        query: view ? { view } : undefined,
      }),
  };
}
